import type {
  CoincidentAggregation,
  XyzFieldSelection,
  ZInterpolation,
} from './types';
import type { SequentialRampName } from './render/colorScale';
import type { RadialMarkType } from './radial/types';
import type { BinCount } from './data/binCounts';

/**
 * Persisted settings for the 3D surface view.
 *
 * The camera is deliberately absent. It is transient interaction state, and persisting it
 * would mean a slide or a saved view reopening at whatever angle someone last dragged it to -
 * including angles where the surface is edge-on and unreadable. Views that want a fixed
 * viewpoint should get an explicit "save current angle" action rather than silent capture.
 */
export type Surface3DViewSettings = XyzFieldSelection & {
  /** How z is estimated where no component sits. */
  interpolation: ZInterpolation;
  /** How several components on one position reduce to a single height. */
  aggregation: CoincidentAggregation;
  /**
   * Samples along each axis of the interpolation lattice.
   *
   * The view's main performance knob, and quadratic: doubling it quadruples both the gridding
   * work and the facet count to depth-sort.
   */
  resolution: number;
  /** Bins along each axis under `binned`, which reads this instead of `resolution`. */
  binCount: BinCount;
  ramp: SequentialRampName;
  /** Colour bands. 0 leaves the ramp continuous. */
  bands: number;
  /** Contour lines laid onto the surface. 0 draws none. */
  contourCount: number;
  showWireframe: boolean;
  showDrapedGrid: boolean;
  showOutlines: boolean;
  showDropLines: boolean;
  showSamplePositions: boolean;
  isLegendActive: boolean;
};

/**
 * What the view opens on.
 *
 * No fields are preselected: which numeric fields mean anything is a question about the
 * customer's model, and guessing produces a confident-looking landscape out of two arbitrary
 * columns. The view shows its empty state until someone chooses.
 */
export const DEFAULT_SURFACE_3D_VIEW_SETTINGS: Surface3DViewSettings = {
  selectedFieldNameX: null,
  selectedFieldNameY: null,
  selectedFieldNameZ: null,
  selectedFieldNameRadius: null,
  selectedFieldNameColor: null,
  interpolation: 'linear',
  aggregation: 'mean',
  /* Fine enough to read as a surface rather than as facets, coarse enough to stay smooth
     while orbiting a large estate. */
  resolution: 64,
  binCount: 'auto',
  ramp: 'blue',
  /* Banded rather than continuous by default: a band edge is an isoline, so banding lets a
     viewer say "one step higher" instead of comparing two similar blues. */
  bands: 9,
  contourCount: 0,
  showWireframe: true,
  showDrapedGrid: false,
  showOutlines: false,
  showDropLines: false,
  showSamplePositions: true,
  isLegendActive: false,
};

/**
 * Persisted settings for the 2D contour view.
 *
 * The same gridding as the surface, read as a map rather than as relief - so it shares the
 * interpolation, aggregation and resolution vocabulary exactly, and drops everything about a
 * camera. `contourCount` matters more here than it does there: on a surface the lines are an
 * extra cue over shading, and on a contour chart they are most of the chart.
 */
export type ContourViewSettings = XyzFieldSelection & {
  interpolation: ZInterpolation;
  aggregation: CoincidentAggregation;
  resolution: number;
  ramp: SequentialRampName;
  /** Colour bands. 0 leaves the ramp continuous. */
  bands: number;
  /** Isolines drawn over the bands. */
  contourCount: number;
  showFilledBands: boolean;
  showIsolines: boolean;
  showSamplePositions: boolean;
  isLegendActive: boolean;
};

export const DEFAULT_CONTOUR_VIEW_SETTINGS: ContourViewSettings = {
  selectedFieldNameX: null,
  selectedFieldNameY: null,
  selectedFieldNameZ: null,
  selectedFieldNameRadius: null,
  selectedFieldNameColor: null,
  interpolation: 'linear',
  aggregation: 'mean',
  resolution: 64,
  ramp: 'blue',
  bands: 9,
  /* Lines as well as bands by default, unlike the surface. A band edge is already an isoline, but
     a drawn line carries a label and a band does not, so this is what makes a value readable off
     the chart rather than only comparable across it.

     One fewer than `bands`, and that is arithmetic rather than taste: n bands have n-1 internal
     edges, and `toThresholds` and the ramp's quantisation only agree at that count. Nine of each
     puts every line through the middle of a band instead of along its boundary - eighteen
     apparent boundaries where there are nine. */
  contourCount: 8,
  showFilledBands: true,
  showIsolines: true,
  showSamplePositions: true,
  isLegendActive: false,
};

/**
 * Persisted settings shared by the 3D scatter and 3D bubble views.
 *
 * One type for both, because the two differ in exactly one thing: whether the radius field varies
 * mark area. That is not in here - it is which view you are looking at, so a host derives it from
 * the view rather than persisting it. A scatter that could be switched into a bubble by a dropdown
 * would make two registrations and two flags pointless.
 *
 * Neither grids anything, so none of the interpolation vocabulary appears here: a point chart
 * draws what it was given and invents nothing between the marks.
 */
export type Points3DViewSettings = XyzFieldSelection & {
  ramp: SequentialRampName;
  bands: number;
  isLegendActive: boolean;
  /** Each mark flattened onto the two back walls, as a side view of the plot. */
  showWallProjections: boolean;
};

export const DEFAULT_POINTS_3D_VIEW_SETTINGS: Points3DViewSettings = {
  selectedFieldNameX: null,
  selectedFieldNameY: null,
  selectedFieldNameZ: null,
  selectedFieldNameRadius: null,
  selectedFieldNameColor: null,
  ramp: 'blue',
  /* Continuous rather than banded, unlike the gridded charts. Banding exists so a viewer can say
     "one step higher" across a filled area; a mark's height is already read off the axis. */
  bands: 0,
  isLegendActive: false,
  showWallProjections: false,
};

/** Persisted settings for the spider view: one shape per selected field, with a spoke per component. */
export type SpiderViewSettings = {
  selectedFieldNames: string[];
  markType: RadialMarkType;
  isLegendActive: boolean;
};

export const DEFAULT_SPIDER_VIEW_SETTINGS: SpiderViewSettings = {
  selectedFieldNames: [],
  markType: 'area',
  isLegendActive: false,
};

/** Persisted settings for the polar view. `selectedFieldNameSeries` may name a field of any type. */
export type PolarViewSettings = {
  selectedFieldNameAngle: string | null;
  selectedFieldNameRadius: string | null;
  selectedFieldNameSeries: string | null;
  markType: RadialMarkType;
  isLegendActive: boolean;
};

export const DEFAULT_POLAR_VIEW_SETTINGS: PolarViewSettings = {
  selectedFieldNameAngle: null,
  selectedFieldNameRadius: null,
  selectedFieldNameSeries: null,
  markType: 'line',
  isLegendActive: false,
};
